import { useMemo } from "react";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { type TargetQueryV4Row } from "../api/target/v4TargetReportByTarget";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { useTargetReportColumns } from "./useTargetReportColumns";
import { KeywordTargetReportTable } from "./KeywordTargetReportTable";
import { idsToVisibilityState } from "../../../components/table/utils";
import { DEFAULT_TARGET_REPORT_SELECTED_COLUMNS, DIMENSION_TYPES } from "../../str/utils/reportingConstants";
import { type AppRow } from "../../../components/table/tableFeatures";

export const GroupedKeywordExpander = ({
  row,
  dimensions,
  baseFilters,
  categoryFilterOptions,
}: {
  row: AppRow<TargetQueryV4Row>;
  dimensions: string;
  baseFilters: ColumnFiltersState;
  categoryFilterOptions: { value: number | string; label: string }[];
}) => {
  const columns = useTargetReportColumns("keyword");

  const rowFilters = useMemo(() => {
    const filters: ColumnFiltersState = [];

    if (row.original.text) {
      filters.push({ id: "text", value: { op: "=", value: row.original.text } });
    }

    if (dimensions === DIMENSION_TYPES.TEXT_WITH_MATCH && row.original.match_type) {
      filters.push({ id: "match_type", value: { op: "=", value: row.original.match_type } });
    }

    return filters;
  }, [row.original.match_type, row.original.text, dimensions]);

  const presetFilters = useMemo(() => {
    return [...baseFilters, ...rowFilters];
  }, [baseFilters, rowFilters]);

  const defaultSelectedColumns = DEFAULT_TARGET_REPORT_SELECTED_COLUMNS;

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const targetReportInitialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["text"],
      end: [],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "targetReportExpanderKeywords",
    initialState: targetReportInitialStateConfig,
  });

  return (
    <KeywordTargetReportTable
      dimensions={DIMENSION_TYPES.KEYWORD_ID}
      state={state}
      initialState={initialState}
      setters={setters}
      categoryFilterOptions={categoryFilterOptions}
      extraFilters={presetFilters}
      disableExpander={true}
      renderDownloadButton={false}
      columns={columns}
    />
  );
};
